
import { memo, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Clock, Timer } from "lucide-react";

interface RestTimesSectionProps {
  exerciseId: string;
  sets: number;
  setRestTime: number;
  exerciseRestTime: number;
  onSetRestTimeChange: (value: string) => void;
  onExerciseRestTimeChange: (value: string) => void;
}

export const RestTimesSection = memo(function RestTimesSection({
  exerciseId,
  sets,
  setRestTime,
  exerciseRestTime,
  onSetRestTimeChange,
  onExerciseRestTimeChange
}: RestTimesSectionProps) {
  // Memoizar opciones de tiempo de descanso entre series
  const setRestTimeOptions = useMemo(() =>
    [15, 30, 45, 60, 90, 120].map((time) => ({
      value: time.toString(),
      label: `${time} segundos`
    })), []
  );

  // Memoizar opciones de tiempo de descanso entre repeticiones (exercise rest time)
  const exerciseRestTimeOptions = useMemo(() =>
    [30, 45, 60, 90, 120, 180, 240, 300].map((time) => ({
      value: time.toString(),
      label: time < 60 ? `${time} segundos` : `${Math.floor(time / 60)} min ${time % 60 ? (time % 60) + 's' : ''}`
    })), []
  );

  if (sets <= 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-[#293233]/10 to-[#253B3D]/10 border border-[#293233]/20 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white">Descansos</h3>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Descanso entre Series */}
        <div className="space-y-2">
          <Label htmlFor={`set-rest-${exerciseId}`} className="text-sm font-medium text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white flex items-center gap-2">
            <Timer className="w-3 h-3 text-primary" />
            Descanso entre Series
          </Label>
          <Select value={setRestTime.toString()} onValueChange={onSetRestTimeChange}>
            <SelectTrigger 
              id={`set-rest-${exerciseId}`}
              className="h-11 border-[black]/30  hover:border-[primary]/50 transition-all duration-200 text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white bg-white/10"
            >
              <SelectValue placeholder="Tiempo de descanso" />
            </SelectTrigger>
            <SelectContent className="bg-primary/30 border-primary/30 z-[100] backdrop-blur-md">
              {setRestTimeOptions.map((option) => (
                <SelectItem 
                  key={option.value} 
                  value={option.value}
                  className="text-white hover:bg-primary/20 focus:bg-primary/30"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Descanso entre Repeticiones (Exercise Rest Time) */}
        <div className="space-y-2">
          <Label htmlFor={`exercise-rest-${exerciseId}`} className="text-sm font-medium text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            Descanso entre Ejercicios
          </Label>
          <Select value={exerciseRestTime.toString()} onValueChange={onExerciseRestTimeChange}>
            <SelectTrigger 
              id={`exercise-rest-${exerciseId}`}
              className="h-11 border-[black]/30  hover:border-[primary]/50 transition-all duration-200 text-primary dark:text-white ocean:text-black forest:text-white ocean:hover:bg-black sunset:text-white bg-white/10"
            >
              <SelectValue placeholder="Tiempo entre repeticiones" />
            </SelectTrigger>
            <SelectContent className="bg-primary/30 border-primary/30 z-[100] backdrop-blur-md">
              {exerciseRestTimeOptions.map((option) => (
                <SelectItem 
                  key={option.value} 
                  value={option.value}
                  className="text-white hover:bg-primary/20 focus:bg-primary/30"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
});
